<template>
  <section class="relative py-20 lg:pb-28 lg:pt-20 px-5vw lg:px-10vw">
    <div
      class="absolute inset-0 transition"
      :class="[ `${color}`, { 'opacity-80': !mobile } ]"
    />

    <Transition name="long-fade">
      <div
        v-show="slug !== highlight.slug && !mobile"
        class="absolute inset-0"
        :class="`${color}`"
      />
    </Transition>
    <!-- purgecss: bg-silver bg-secondary -->
    <div class="relative">
      <!-- The main highlight content banner and primary video -->
      <div
        data-aos="fade-up"
        class="relative flex-c w-full h-50vw lg:h-38vw shadow-xl overflow-hidden"
      >
        <youtube
          ref="youtube"
          :video-id="highlight.primaryVideo.video"
          :player-vars="playerVars"
          :width="'100%'"
          :height="'100%'"
          class="absolute inset-0"
          @playing="playing"
          @ended="ended"
        />
        <Transition name="fade">
          <!-- Overlay for the youtube container @click to show the vid -->
          <button
            v-show="!videoActive"
            class="absolute inset-0 h-full w-full bg-center bg-cover"
            :style="`background-image: url('${highlight.primaryVideo.image.sizes.large}')`"
            @click="playVideo(highlight.primaryVideo.video)"
          >
            <!-- Image zoom on hover falls back to background image for ie11 -->
            <img
              v-if="browser !== 'IE'"
              class="object-cover h-full w-full"
              :class="{ 'hover:zoom transition': !touch }"
              :src="highlight.primaryVideo.image.sizes.large"
            >
            <div class="absolute pin-c bg-white rounded-full opacity-80 h-12 w-12 md:h-24 md:w-24">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" v-bind:svg-inline="''" v-bind:class="'absolute pin-c h-16 w-16 md:h-32 md:w-32 fill-current text-primary'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M30.662 5.003C26.174 4.358 21.214 4 16 4S5.826 4.358 1.338 5.003C.478 8.369 0 12.089 0 16s.477 7.63 1.338 10.997C5.827 27.642 10.786 28 16 28s10.174-.358 14.662-1.003C31.522 23.631 32 19.911 32 16s-.477-7.63-1.338-10.997zM12 22V10l10 6-10 6z"/></svg>
            </div>
            <!-- The video title -->
            <div class="absolute top-0 left-0 leading-none font-medium text-left">
              <span
                class="inline-block px-2 py-1 md:px-5 md:py-4 xl:px-8 xl:pt-4 xxl:pt-5 bg-white"
              >
                <h2
                  class="text-sm md:text-3xl xxl:text-4xl"
                  v-html="highlight.primaryVideo.title"
                />
              </span>
              <br>
              <span
                class="inline-flex items-center px-2 py-1 md:px-5 md:py-2 lg:pt-0 xl:px-8 xl:pb-3 xxl:pb-4 bg-white text-primary text-sm md:text-3xl xxl:text-4xl"
              >
                {{ highlight.primaryVideo.typeHeading }}
                <IconBase
                  :icon="`icon${highlight.primaryVideo.type}`"
                  :size="$mq === 'xs' || $mq === 'sm' ? '15px' : '35px'"
                  class="ml-2 md:ml-6 mb-px"
                />
              </span>
            </div>
          </button>
        </Transition>
      </div>

      <div class="lg:flex my-4vw">
        <!-- The highlight header/description box  -->
        <div
          data-aos="fade-up"
          class="lg:w-40vw p-5 lg:px-8 lg:py-10 bg-white shadow-xl"
        >
          <h1
            class="mb-3 lg:mb-5 text-primary lg:text-xl font-bold uppercase"
            v-html="highlight.title"
          />
          <p
            class="lg:text-xl font-light"
            v-html="highlight.description"
          />
        </div>

        <!-- The explore 360 button -->
        <ButtonMain
          data-aos="fade-up"
          :icon="'icontour'"
          :size="'32px'"
          :path="highlight.panorama[0].entry.post_name"
          :cta="highlight.panorama[0].button"
          class="p-4 mt-4vw mx-auto w-full lg:w-35% lg:self-end"
        />
      </div>

      <!-- The content slider and control buttons on desktop -->
      <div
        v-show="this.$mq === 'xl' || this.$mq === 'xxl'"
        data-aos="fade-up"
        class="relative"
      >
        <button
          v-if="highlight.children.length >= 4"
          class="absolute pin-c-y -ml-12 z-10 bg-primary flex-c h-10 w-10 rounded-sm transition"
          :class="{ 'opacity-25': swiperInfo.isBeginning, 'hover:bg-black': !touch }"
          @click="slidePrevious"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="59" height="32" viewBox="0 0 59 32" v-bind:svg-inline="''" v-bind:class="'h-8 w-8 fill-current text-white rotate-90'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M58.471 1.402l-.937-.937a1.583 1.583 0 00-2.244 0L29.477 26.289 3.653.465a1.583 1.583 0 00-2.244 0l-.937.937a1.583 1.583 0 000 2.244l27.876 27.876a1.583 1.583 0 002.244 0L58.468 3.646a1.573 1.573 0 00.003-2.245z"/></svg>
        </button>
        <button
          v-if="highlight.children.length >= 4"
          class="absolute pin-c-y right-0 -mr-12 z-10 bg-primary flex-c h-10 w-10 rounded-sm transition"
          :class="{ 'opacity-25': swiperInfo.isEnd, 'hover:bg-black': !touch }"
          @click="slideNext"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="59" height="32" viewBox="0 0 59 32" v-bind:svg-inline="''" v-bind:class="'h-8 w-8 fill-current text-white rotate-270'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M58.471 1.402l-.937-.937a1.583 1.583 0 00-2.244 0L29.477 26.289 3.653.465a1.583 1.583 0 00-2.244 0l-.937.937a1.583 1.583 0 000 2.244l27.876 27.876a1.583 1.583 0 002.244 0L58.468 3.646a1.573 1.573 0 00.003-2.245z"/></svg>
        </button>
        <!-- The swiper -->
        <swiper
          ref="mySwiper"
          :options="swiperOption"
          class="z-0"
        >
          <swiper-slide
            v-for="card in highlight.children"
            :key="card.slug"
          >
            <!-- The cards -->
            <Card
              :card="card"
              :path="`${highlight.slug}/${card.slug}`"
            />
          </swiper-slide>
        </swiper>
      </div>

      <!-- Content cards on mobile instead of the slider -->
      <div
        v-if="this.$mq === 'sm' || this.$mq === 'md' || this.$mq === 'lg'"
        class="flex flex-wrap justify-between"
      >
        <Card
          v-for="card in highlight.children"
          :key="card.slug"
          data-aos="zoom-in-up"
          :card="card"
          :path="`${highlight.slug}/${card.slug}`"
        />
      </div>

      <!-- The highlight external cta button -->
      <div class="flex flex-c">
        <ButtonMain
          data-aos="fade-up"
          :type="'external'"
          :cta="highlight.linkout_cta"
          :path="utmHref(highlight.linkout, 'tier-2-cta')"
          :icon="'iconlinkout'"
          class="py-4 px-8 mt-3vw"
        />
      </div>
    </div>
  </section>
</template>

<script>
import 'swiper/dist/css/swiper.css'
import { swiper, swiperSlide } from 'vue-awesome-swiper'
import { mapState } from 'vuex'
import ButtonMain from '@/components/buttons/ButtonMain.vue'
import IconBase from '@/components/icons/IconBase.vue'
import Card from '@/components/Card.vue'

export default {
  name: 'Hightlight',

  components: {
    swiper,
    swiperSlide,
    ButtonMain,
    IconBase,
    Card
  },

  props: {
    highlight: {
      type: Object,
      default: () => {}
    },
    color: {
      type: String,
      default: 'bg-secondary'
    }
  },

  data: () => ({
    videoActive: false,
    publicPath: process.env.BASE_URL,
    playerVars: {
      rel: 0,
      modestbranding: 1,
      allowfullscreen: false,
      // Youtube iframe api always set domain origin for security
      origin: window.location.origin
    },
    swiperInfo: {
      isBeginning: true,
      isEnd: false
    },
    swiperOption: {
      breakpointsInverse: true,
      centerInsufficientSlides: true,
      simulateTouch: false,
      setWrapperSize: true,
      breakpoints: {
        1024: {
          slidesPerView: 3,
          spaceBetween: 20
        },
        1280: {
          slidesPerView: 4,
          spaceBetween: 20
        }
      }
    }
  }),

  computed: {
    ...mapState([ 'mobile', 'touch', 'browser', 'video', 'noScroll', 'slug' ]),

    player() {
      return this.$refs.youtube.player
    },

    swiper() {
      return this.$refs.mySwiper.swiper
    }
  },

  watch: {
    video() {
      if (this.video !== this.highlight.primaryVideo.video) {
        this.stopVideo()
      }
    },
    noScroll() {
      this.stopVideo()
    }
  },

  mounted() {
    this.swiperInfo.isEnd = this.swiper.isEnd

    this.swiper.on('progress', () => {
      this.swiperInfo.isEnd = this.swiper.isEnd
      this.swiperInfo.isBeginning = this.swiper.isBeginning
    })
  },

  methods: {
    slidePrevious() {
      this.swiper.slidePrev()
    },
    slideNext() {
      this.swiper.slideNext()
    },
    playVideo(video) {
      this.player.playVideo()
      this.videoActive = true
      this.$store.commit('video', video)
    },
    stopVideo() {
      this.player.stopVideo()
      this.videoActive = false
    },
    playing() {

    },
    ended() {
      this.videoActive = false
    }
  }
}
</script>
